// Cliente HTTP: envía la cabecera CSRF en toda petición que modifica datos,
// el id del socket en vivo (para no recibir el eco de los propios cambios) y
// convierte los errores del servidor en ApiError con detalle por campo.

import { lang, t } from './i18n';
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
  /** Errores de validación como { "campo.sub": "mensaje" }. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const d of this.details as { path?: string; message?: string }[]) {
        if (d && typeof d.path === 'string' && d.message && !(d.path in out)) out[d.path] = d.message;
      }
    }
    return out;
  }
}

let liveSocketId: string | null = null;
export function setLiveSocketId(id: string | null) {
  liveSocketId = id;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type Listener = (e: ApiError) => void;
const unauthorizedListeners = new Set<Listener>();
export function onUnauthorized(fn: Listener) {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

export async function api<T = unknown>(path: string, opts: { method?: Method; body?: unknown; signal?: AbortSignal; headers?: Record<string, string> } = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (method !== 'GET') headers['X-CSRF'] = '1';
  if (liveSocketId) headers['X-Live-Socket-ID'] = liveSocketId;
  // El servidor responde los mensajes de error en el idioma de la interfaz.
  headers['Accept-Language'] = lang();
  let payload: BodyInit | undefined;
  if (opts.body instanceof FormData) payload = opts.body;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: payload, credentials: 'same-origin', signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'network', t('No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.'));
  }
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    const e = new ApiError(res.status, err?.code ?? 'error', err?.message ?? t('Error {status}', { status: res.status }), err?.details);
    if (res.status === 401) unauthorizedListeners.forEach((fn) => fn(e));
    throw e;
  }
  return json as T;
}

/**
 * Subir un archivo mostrando el avance (fetch no lo informa). Mismas cabeceras y errores que api().
 */
export function uploadWithProgress<T = unknown>(path: string, body: FormData, onProgress: (fraction: number) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.withCredentials = true;
    xhr.setRequestHeader('X-CSRF', '1');
    xhr.setRequestHeader('Accept-Language', lang());
    if (liveSocketId) xhr.setRequestHeader('X-Live-Socket-ID', liveSocketId);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onerror = () => reject(new ApiError(0, 'network', t('No hay conexión con el servidor. Revisa tu internet e intenta de nuevo.')));
    xhr.onload = () => {
      let json: unknown = null;
      try { json = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { json = null; }
      if (xhr.status >= 200 && xhr.status < 300) { resolve(json as T); return; }
      const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
      // El proxy rechaza los archivos demasiado grandes antes de llegar a la plataforma.
      const message = xhr.status === 413 ? t('El archivo supera el tamaño máximo de 100 MB') : err?.message ?? t('Error {status}', { status: xhr.status });
      const e = new ApiError(xhr.status, err?.code ?? 'error', message, err?.details);
      if (xhr.status === 401) unauthorizedListeners.forEach((fn) => fn(e));
      reject(e);
    };
    xhr.send(body);
  });
}

export const get = <T = unknown>(path: string, signal?: AbortSignal) => api<T>(path, { signal });
export const post = <T = unknown>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ?? {} });
export const put = <T = unknown>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body: body ?? {} });
export const patch = <T = unknown>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body: body ?? {} });
export const del = <T = unknown>(path: string) => api<T>(path, { method: 'DELETE' });

/** Construye una URL con parámetros, omitiendo los vacíos. */
export function withQuery(path: string, params: Record<string, string | number | boolean | null | undefined>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}
